import React, { useState } from "react";
import "./Skill.css";
import useScrollReveal from "../../hooks/useScrollReveal";
import {
  SiReact, SiNodedotjs, SiPostgresql, SiMongodb, SiRedis,
  SiTypescript, SiJavascript, SiDocker, SiGithub, SiPostman,
  SiPrisma, SiNestjs, SiExpress, SiNextdotjs, SiTailwindcss,
  SiAmazonaws, SiGit, SiSocketdotio, SiOpenai,
} from "react-icons/si";
import { FaJava } from "react-icons/fa";

const skillCategories = {
  Frontend: [
    { icon: <SiReact style={{ color: "#61dafb" }} />, name: "React.js", pct: 90 },
    { icon: <SiNextdotjs style={{ color: "#e2e8f0" }} />, name: "Next.js", pct: 70 },
    { icon: <SiJavascript style={{ color: "#f7df1e" }} />, name: "JavaScript", pct: 88 },
    { icon: <SiTypescript style={{ color: "#3178c6" }} />, name: "TypeScript", pct: 78 },
    { icon: <SiTailwindcss style={{ color: "#38bdf8" }} />, name: "Tailwind CSS", pct: 82 },
  ],
  Backend: [
    { icon: <SiNodedotjs style={{ color: "#68a063" }} />, name: "Node.js", pct: 85 },
    { icon: <SiNestjs style={{ color: "#e0234e" }} />, name: "NestJS", pct: 75 },
    { icon: <SiExpress style={{ color: "#94a3b8" }} />, name: "Express.js", pct: 85 },
    { icon: <FaJava style={{ color: "#f89820" }} />, name: "Java", pct: 60 },
  ],
  Database: [
    { icon: <SiPostgresql style={{ color: "#336791" }} />, name: "PostgreSQL", pct: 82 },
    { icon: <SiMongodb style={{ color: "#47a248" }} />, name: "MongoDB", pct: 78 },
    { icon: <SiRedis style={{ color: "#dc382d" }} />, name: "Redis", pct: 72 },
    { icon: <SiPrisma style={{ color: "#a78bfa" }} />, name: "Prisma", pct: 80 },
  ],
  Cloud: [
    { icon: <SiAmazonaws style={{ color: "#ff9900" }} />, name: "AWS EC2", pct: 80 },
    { icon: <SiAmazonaws style={{ color: "#ff9900" }} />, name: "RDS/Aurora", pct: 75 },
    { icon: <SiAmazonaws style={{ color: "#ff9900" }} />, name: "Lambda/S3", pct: 72 },
    { icon: <SiDocker style={{ color: "#2496ed" }} />, name: "Docker", pct: 68 },
  ],
};

const categoryDescriptions = {
  Frontend: "UI & client-side",
  Backend: "Server & APIs",
  Database: "Storage & ORM",
  Cloud: "Infra & DevOps",
};

const tools = [
  { icon: <SiGit style={{ color: "#f05032" }} />, name: "Git" },
  { icon: <SiGithub style={{ color: "#94a3b8" }} />, name: "GitHub" },
  { icon: <SiPostman style={{ color: "#ff6c37" }} />, name: "Postman" },
  { icon: <SiSocketdotio style={{ color: "#94a3b8" }} />, name: "Socket.io" },
  { icon: <SiOpenai style={{ color: "#74aa9c" }} />, name: "OpenAI API" },
  { icon: <SiRedis style={{ color: "#dc382d" }} />, name: "BullMQ" },
  { icon: <SiDocker style={{ color: "#2496ed" }} />, name: "Docker" },
  { icon: <SiAmazonaws style={{ color: "#ff9900" }} />, name: "AWS SQS" },
];

const Skill = () => {
  const [activeTab, setActiveTab] = useState("Frontend");
  const currentSkills = skillCategories[activeTab];
  const layoutRef = useScrollReveal({ threshold: 0.05 });
  const toolsRef = useScrollReveal({ threshold: 0.05 });

  return (
    <div className="skill-main" id="skills">
      <h2 className="section-title">
        My <span>Skills</span>
      </h2>

      <div className="skill-layout reveal" ref={layoutRef}>
        {/* ── LEFT: icon grid + tabs ── */}
        <div className="skill-left-panel">
          <div className="skill-categories">
            {Object.keys(skillCategories).map((cat) => (
              <button
                key={cat}
                className={`skill-tab ${activeTab === cat ? "active" : ""}`}
                onClick={() => setActiveTab(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="skills-grid">
            {currentSkills.map((skill) => (
              <div className="skill-card" key={skill.name}>
                <span className="skill-card-icon">{skill.icon}</span>
                <span className="skill-card-name">{skill.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT: premium bars ── */}
        <div className="skill-right-panel">
          <div className="skill-right-header">
            <p className="skill-right-label">{categoryDescriptions[activeTab]}</p>
            <h3 className="skill-right-title">
              {activeTab} <span>Proficiency</span>
            </h3>
          </div>

          <div className="skill-bar-list">
            {currentSkills.map((skill) => (
              <div className="skill-bar-row" key={skill.name + "-bar"}>
                <div className="skill-bar-meta">
                  <div className="skill-bar-info">
                    <div className="skill-bar-icon-wrap">{skill.icon}</div>
                    <span className="skill-bar-label">{skill.name}</span>
                  </div>
                  <span className="skill-bar-pct">{skill.pct}%</span>
                </div>
                <div className="skill-bar-track">
                  {/* key forces re-animation on tab switch */}
                  <div
                    key={activeTab + skill.name}
                    className="skill-bar-fill"
                    style={{ width: `${skill.pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── TOOLS STRIP ── */}
      <div className="tools-strip reveal" ref={toolsRef}>
        <div className="tools-strip-header">
          <span className="tools-strip-label">Tools & Platforms</span>
          <div className="tools-strip-line" />
        </div>
        <div className="tools-grid">
          {tools.map((tool) => (
            <div className="tool-pill" key={tool.name}>
              <span className="tool-pill-icon">{tool.icon}</span>
              {tool.name}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Skill;
